export default class DateTimeComponents {

  public readonly year: number;
  public readonly month: number;
  public readonly day: number;
  public readonly hour: number;
  public readonly minute: number;
  public readonly second: number;

  constructor(year: string|number, month: string|number, day: string|number, hour: string|number, minute: string|number, second: string|number) {
    this.year = parseInt(`${year}`, 10);
    this.month = parseInt(`${month}`, 10);
    this.day = parseInt(`${day}`, 10);
    this.hour = parseInt(`${hour}`, 10);
    this.minute = parseInt(`${minute}`, 10);
    this.second = parseInt(`${second}`, 10);
  }

  static now(): DateTimeComponents {
    return DateTimeComponents.fromDate(new Date());
  }

  static fromDate(date: Date): DateTimeComponents {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hour = date.getHours();
    const minute = date.getMinutes();
    const second = date.getSeconds();
    return new DateTimeComponents(year, month, day, hour, minute, second);
  }

  static fromYYYYMMDD(yyyymmdd: string): DateTimeComponents|null {
    const matches = /^([0-9]{4,4})([0-9]{2,2})([0-9]{2,2})$/.exec(yyyymmdd);
    if (matches === null) return null;
    const year = parseInt(matches[1], 10);
    const month = parseInt(matches[2], 10);
    const day = parseInt(matches[3], 10);
    return new DateTimeComponents(year, month, day, 0, 0, 0);
  }

  static fromISO8601(value: string): DateTimeComponents|null {
    const matches = /^([0-9]{4,4})-([0-9]{2,2})-([0-9]{2,2})(?:T([0-9]{2,2}):([0-9]{2,2}):([0-9]{2,2})([+-][0-9]{2,2}:?[0-9]{2,2})?)?$/.exec(value);
    if (matches === null) return null;
    const year = parseInt(matches[1], 10);
    const month = parseInt(matches[2], 10);
    const day = parseInt(matches[3], 10);
    let hour = 0;
    let minute = 0;
    let second = 0;
    if (typeof matches[4] !== 'undefined') {
      hour = parseInt(matches[4], 10);
      minute = parseInt(matches[5], 10);
      second = parseInt(matches[6], 10);
    }
    return new DateTimeComponents(year, month, day, hour, minute, second);
  }

  isValidDateTime(): boolean {
    if (this.day < 1 || this.day > 31) {
      return false;
    }
    if (this.month < 1 || this.month > 12) {
      return false;
    }
    if (this.month === 4 || this.month === 6 || this.month === 9 || this.month === 11) {
      if (this.day > 30) {
        return false;
      }
    }
    if (this.month === 2) {
      if (this.day > 29) {
        return false;
      }
      if (this.day === 28) {
        if (this.year % 400 === 0 || (this.year % 100 !== 0 && this.year % 4 === 0)) {
          return false;
        }
      }
    }
    if (this.hour < 0 || this.hour >= 24) {
      return false;
    }
    if (this.minute < 0 || this.minute >= 60) {
      return false;
    }
    if (this.second < 0 || this.second >= 60) {
      return false;
    }
    return true;
  }

  earlierThan(other: DateTimeComponents): boolean {
    if (this.year < other.year) return true;
    if (this.year > other.year) return false;
    if (this.month < other.month) return true;
    if (this.month > other.month) return false;
    if (this.day < other.day) return true;
    return false;
  }

  toDate(): Date {
    const date = new Date(2000, 0, 1); //  avoid unexpected month carry-over
    date.setDate(this.day);
    date.setMonth(this.month - 1);
    date.setFullYear(this.year);
    date.setHours(this.hour);
    date.setMinutes(this.minute);
    date.setSeconds(this.second);
    return date;
  }

  format(format: string): string {
    const pad = (number: number, zeros: number) => {
      let value = `${number}`;
      while (value.length < zeros) {
        value = `0${value}`;
      }
      return value;
    };
    let value = format;
    value = value.replace('Y', pad(this.year, 4));
    value = value.replace('m', pad(this.month, 2));
    value = value.replace('n', `${this.month}`);
    value = value.replace('d', pad(this.day, 2));
    value = value.replace('j', `${this.day}`);
    value = value.replace('H', pad(this.hour, 2));
    value = value.replace('i', pad(this.minute, 2));
    value = value.replace('s', pad(this.second, 2));
    return value;
  }
}
